import { useState } from 'react';
import Modal from '@/components/ui/Modal';
import {
  CONTRATOS_CON_FIN,
  JORNADA_LABEL,
  TIPO_CONTRATO_LABEL,
  type Contrato,
  type DocumentoEmpleado,
  type Jornada,
  type TipoContrato,
} from '@/lib/personal';

/**
 * Alta y edición de un contrato.
 *
 * La fecha de fin se pide solo cuando el tipo la exige (temporal, formación,
 * prácticas). El servidor lo vuelve a comprobar: esto es comodidad, no
 * validación.
 *
 * El salario es opcional y se avisa de que es sensible. No se muestra el que ya
 * hubiera guardado —eso vive detrás de `/sensible`—: al editar un contrato se
 * deja en blanco y solo se escribe si se teclea algo, para que abrir y guardar
 * sin tocarlo no lo borre.
 */
interface Props {
  abierto: boolean;
  contrato: Contrato | null;
  documentos: DocumentoEmpleado[];
  onCerrar: () => void;
  onGuardar: (datos: Record<string, unknown>) => Promise<void>;
}

const TIPOS = Object.keys(TIPO_CONTRATO_LABEL) as TipoContrato[];

export default function ModalContrato({ abierto, contrato, documentos, onCerrar, onGuardar }: Props) {
  const [tipo, setTipo] = useState<TipoContrato>(contrato?.tipo || 'indefinido');
  const [jornada, setJornada] = useState<Jornada>(contrato?.jornada || 'completa');
  const [horasSemana, setHorasSemana] = useState(contrato?.horasSemana ? String(contrato.horasSemana) : '');
  const [fechaInicio, setFechaInicio] = useState(contrato?.fechaInicio || '');
  const [fechaFin, setFechaFin] = useState(contrato?.fechaFin || '');
  const [periodoPruebaHasta, setPeriodoPrueba] = useState(contrato?.periodoPruebaHasta || '');
  const [categoria, setCategoria] = useState(contrato?.categoria || '');
  const [salario, setSalario] = useState('');
  const [documentoId, setDocumentoId] = useState(contrato?.documentoId || '');
  const [nota, setNota] = useState(contrato?.nota || '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exigeFin = CONTRATOS_CON_FIN.includes(tipo);
  const contratosSubidos = documentos.filter((d) => d.tipo === 'contrato');

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setError(null);
    try {
      await onGuardar({
        tipo,
        jornada,
        horasSemana: horasSemana ? Number(horasSemana) : undefined,
        fechaInicio,
        fechaFin: exigeFin || fechaFin ? fechaFin || undefined : undefined,
        periodoPruebaHasta: periodoPruebaHasta || undefined,
        categoria: categoria.trim() || undefined,
        salarioBrutoAnual: salario ? Number(salario) : undefined,
        documentoId: documentoId || undefined,
        nota: nota.trim() || undefined,
      });
      onCerrar();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen={abierto} onClose={onCerrar} title={contrato ? 'Editar contrato' : 'Nuevo contrato'} size="lg">
      <form onSubmit={enviar} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs font-medium text-sereno-dark/60">Tipo</span>
            <select className="input mt-1" value={tipo} onChange={(e) => setTipo(e.target.value as TipoContrato)}>
              {TIPOS.map((t) => (
                <option key={t} value={t}>{TIPO_CONTRATO_LABEL[t]}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-sereno-dark/60">Jornada</span>
            <select className="input mt-1" value={jornada} onChange={(e) => setJornada(e.target.value as Jornada)}>
              {(Object.keys(JORNADA_LABEL) as Jornada[]).map((j) => (
                <option key={j} value={j}>{JORNADA_LABEL[j]}</option>
              ))}
            </select>
          </label>
        </div>

        {jornada === 'parcial' && (
          <label className="block">
            <span className="text-xs font-medium text-sereno-dark/60">Horas por semana</span>
            <input
              type="number" min={1} max={60} className="input mt-1"
              value={horasSemana} onChange={(e) => setHorasSemana(e.target.value)}
            />
          </label>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs font-medium text-sereno-dark/60">Empieza el *</span>
            <input type="date" required className="input mt-1" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-sereno-dark/60">
              Acaba el {exigeFin && '*'}
            </span>
            <input
              type="date" required={exigeFin} className="input mt-1"
              value={fechaFin} onChange={(e) => setFechaFin(e.target.value)}
            />
            {!exigeFin && (
              <span className="text-[11px] text-sereno-dark/40">Déjalo vacío si no tiene fecha de fin.</span>
            )}
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs font-medium text-sereno-dark/60">Periodo de prueba hasta</span>
            <input type="date" className="input mt-1" value={periodoPruebaHasta} onChange={(e) => setPeriodoPrueba(e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-sereno-dark/60">Categoría profesional</span>
            <input
              type="text" className="input mt-1" placeholder="La del convenio"
              value={categoria} onChange={(e) => setCategoria(e.target.value)}
            />
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs font-medium text-sereno-dark/60">Salario bruto anual (€)</span>
            <input
              type="number" min={0} step={100} className="input mt-1"
              placeholder={contrato?.tieneSalario ? 'Guardado — escribe para cambiarlo' : 'Opcional'}
              value={salario} onChange={(e) => setSalario(e.target.value)}
            />
            <span className="text-[11px] text-sereno-dark/40">Dato sensible: no sale en listas ni en el export.</span>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-sereno-dark/60">Contrato firmado</span>
            <select className="input mt-1" value={documentoId} onChange={(e) => setDocumentoId(e.target.value)}>
              <option value="">— Ninguno —</option>
              {contratosSubidos.map((d) => (
                <option key={d.id} value={d.id}>{d.fileName}</option>
              ))}
            </select>
            {contratosSubidos.length === 0 && (
              <span className="text-[11px] text-sereno-dark/40">Súbelo en Documentos con el tipo «Contrato».</span>
            )}
          </label>
        </div>

        <label className="block">
          <span className="text-xs font-medium text-sereno-dark/60">Nota</span>
          <textarea className="input mt-1" rows={2} value={nota} onChange={(e) => setNota(e.target.value)} />
        </label>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCerrar} className="btn-secondary">Cancelar</button>
          <button type="submit" disabled={guardando} className="btn-primary disabled:opacity-50">
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
